/**
 * Per-user, per-application shell preferences: the chosen layout, Pinned and
 * Recent. Stored on the SERVER (the user-memory store's `__shell__` record —
 * `GET/PUT …/user-memory/shell` on every host) and mirrored to localStorage so
 * the first paint already wears the user's layout instead of flashing classic.
 *
 * The browser never decides what a user may see from here: Pinned and Recent
 * hold object names only, and the shell filters them against the rights-
 * filtered navigation on every render.
 */
import { SHELL_LIMITS, isLayoutId, type LayoutId } from './config'

export interface PinnedEntry { obj_name: string }
export interface RecentEntry { obj_name: string; at: number }

export type LayoutPrefValue = string | number | boolean
/** Small per-layout settings (the macOS desktop's Dock side, magnification…). */
export type LayoutPrefs = Partial<Record<LayoutId, Record<string, LayoutPrefValue>>>

export interface ShellPrefsDoc {
  v: 1
  layout: LayoutId | ''
  pinned: PinnedEntry[]
  recent: RecentEntry[]
  layoutPrefs?: LayoutPrefs
  updated_at?: number
}

/** How a host reads and writes the server record. Either may reject; the
 *  shell then keeps working on the local mirror alone. */
export interface ShellPrefsTransport {
  load(): Promise<Partial<ShellPrefsDoc> | null>
  save(doc: ShellPrefsDoc): Promise<unknown>
  /** The desktop picture (an image data URL), its own record so the prefs
   *  document stays small. Absent ⇒ only the built-in pictures are offered. */
  loadWallpaper?(): Promise<string | null>
  saveWallpaper?(image: string | null): Promise<unknown>
}

export const EMPTY_PREFS: ShellPrefsDoc = { v: 1, layout: '', pinned: [], recent: [] }

const OBJ_NAME_MAX = 200

/** A clean document from anything (a server answer, a stale mirror, junk). Never throws. */
export function normalisePrefs(raw: unknown, caps?: { pinned?: number; recent?: number }): ShellPrefsDoc {
  const r = (raw && typeof raw === 'object') ? raw as Record<string, unknown> : {}
  const name = (v: unknown) => (typeof v === 'string' ? v.trim().slice(0, OBJ_NAME_MAX) : '')
  const pinned: PinnedEntry[] = []
  const seenP = new Set<string>()
  for (const e of Array.isArray(r.pinned) ? r.pinned : []) {
    const n = name(typeof e === 'string' ? e : (e as PinnedEntry | null)?.obj_name)
    if (n && !seenP.has(n)) { seenP.add(n); pinned.push({ obj_name: n }) }
  }
  const recent: RecentEntry[] = []
  const seenR = new Set<string>()
  for (const e of Array.isArray(r.recent) ? r.recent : []) {
    const o = (e && typeof e === 'object') ? e as Partial<RecentEntry> : {}
    const n = name(o.obj_name)
    const at = typeof o.at === 'number' && Number.isFinite(o.at) ? Math.round(o.at) : 0
    if (n && !seenR.has(n)) { seenR.add(n); recent.push({ obj_name: n, at }) }
  }
  const layoutPrefs = normaliseLayoutPrefs(r.layoutPrefs)
  return {
    v: 1,
    layout: isLayoutId(r.layout) ? r.layout : '',
    ...(Object.keys(layoutPrefs).length ? { layoutPrefs } : {}),
    pinned: caps?.pinned ? pinned.slice(0, caps.pinned) : pinned,
    recent: caps?.recent ? recent.slice(0, caps.recent) : recent,
    updated_at: typeof r.updated_at === 'number' ? r.updated_at : undefined,
  }
}

const PREF_KEY = /^[A-Za-z][A-Za-z0-9_]{0,31}$/

/** The same rules the server applies (shell_prefs.normalise / ShellPrefs.normalise). */
export function normaliseLayoutPrefs(raw: unknown): LayoutPrefs {
  const out: LayoutPrefs = {}
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out
  for (const [id, map] of Object.entries(raw as Record<string, unknown>)) {
    if (!isLayoutId(id) || !map || typeof map !== 'object' || Array.isArray(map)) continue
    const clean: Record<string, LayoutPrefValue> = {}
    let n = 0
    for (const [k, v] of Object.entries(map as Record<string, unknown>)) {
      if (n >= SHELL_LIMITS.layoutPrefKeysMax) break
      if (!PREF_KEY.test(k)) continue
      if (typeof v === 'boolean') clean[k] = v
      else if (typeof v === 'number' && Number.isFinite(v)) clean[k] = v
      else if (typeof v === 'string') clean[k] = v.trim().slice(0, SHELL_LIMITS.layoutPrefStringMax)
      else continue
      n += 1
    }
    if (Object.keys(clean).length) out[id] = clean
  }
  return out
}

export function mirrorKey(appKey: string, userKey: string): string {
  return `twasta:shell:${appKey}:${userKey}`
}

export function readMirror(appKey: string, userKey: string): ShellPrefsDoc | null {
  try {
    const raw = localStorage.getItem(mirrorKey(appKey, userKey))
    return raw ? normalisePrefs(JSON.parse(raw)) : null
  } catch {
    return null
  }
}

export function writeMirror(appKey: string, userKey: string, doc: ShellPrefsDoc): void {
  try {
    localStorage.setItem(mirrorKey(appKey, userKey), JSON.stringify(doc))
  } catch {
    /* private mode / quota: the server copy still holds it */
  }
}

/** Move `objName` to the top of Recent, capped. */
export function pushRecent(recent: RecentEntry[], objName: string, at: number, cap: number): RecentEntry[] {
  return [{ obj_name: objName, at }, ...recent.filter((r) => r.obj_name !== objName)].slice(0, cap)
}

export function togglePinned(pinned: PinnedEntry[], objName: string, cap: number): PinnedEntry[] {
  if (pinned.some((p) => p.obj_name === objName)) return pinned.filter((p) => p.obj_name !== objName)
  return [...pinned, { obj_name: objName }].slice(0, cap)
}

/** Move a pinned entry by `delta` places (reorder). */
export function movePinned(pinned: PinnedEntry[], objName: string, delta: number): PinnedEntry[] {
  const i = pinned.findIndex((p) => p.obj_name === objName)
  if (i < 0) return pinned
  const j = Math.max(0, Math.min(pinned.length - 1, i + delta))
  if (i === j) return pinned
  const next = [...pinned]
  const [e] = next.splice(i, 1)
  next.splice(j, 0, e)
  return next
}

/**
 * Rights filtering: the stored entries a user may still see — only screens in
 * `nav`, which the host has already filtered by roles and grants. The stored
 * list is never rewritten here (a grant restored tomorrow brings the entry
 * back); it is simply never SHOWN while the screen is out of reach.
 */
export function visibleEntries<E extends { obj_name: string }, I extends { obj_name: string; obj_type: string }>(
  entries: E[], nav: I[], find: (nav: I[], objName: string) => I | null,
): Array<{ entry: E; item: I }> {
  const out: Array<{ entry: E; item: I }> = []
  for (const e of entries) {
    const item = find(nav, e.obj_name)
    if (item && item.obj_type !== '-') out.push({ entry: e, item })
  }
  return out
}
